import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ENFORCEMENT_LABELS } from '../mock';
import { parsePolicy, findConflicts } from '../lib/policyEngine';
import { Bot, AlertTriangle, ShieldCheck, Check, Wand2 } from 'lucide-react';
import { toast } from '../lib/aiToast';

const ENF = ['off', 'warn', 'enforce'];
const EXAMPLES = [
  'Block scheduling for new patients until insurance is photographed',
  "Don't require a referral for HMO plans",
  'Require a card on file when remaining deductible is over $500',
  'Warn the front desk to collect a $250 deposit at check-in',
];

const enfTone = (e, active) => active
  ? (e === 'enforce' ? 'bg-brand-600 text-white shadow-sm' : e === 'warn' ? 'bg-amber-500 text-white shadow-sm' : 'bg-slate-500 text-white shadow-sm')
  : 'text-slate-500 hover:text-slate-800';

export default function NewPolicyDialog({ open, onOpenChange, existing = [] }) {
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [scope, setScope] = useState('');
  const [enf, setEnf] = useState('warn');
  const [ack, setAck] = useState(false);

  const parsed = useMemo(() => parsePolicy(text), [text]);
  const interpreted = text.trim().length > 6;

  // Sync editable fields whenever the interpretation changes (i.e., on text edits).
  useEffect(() => {
    if (!interpreted) return;
    setName(parsed.name);
    setScope(parsed.scope);
    setEnf(parsed.enforcement);
    setAck(false);
  }, [parsed.name, parsed.scope, parsed.enforcement, interpreted]);

  const conflicts = useMemo(
    () => (interpreted ? findConflicts({ ...parsed, enforcement: enf }, existing) : []),
    [parsed, enf, existing, interpreted]
  );
  const contradictions = conflicts.filter((c) => c.severity === 'contradiction');
  const overlaps = conflicts.filter((c) => c.severity === 'overlap');
  const blocked = contradictions.length > 0 && !ack;
  const canAdd = interpreted && name.trim() && !blocked;

  const reset = () => { setText(''); setName(''); setScope(''); setEnf('warn'); setAck(false); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  // NOTE: the v1 API exposes no create-policy endpoint (only GET /policies and
  // PATCH /policies/{id}). The plain-English drafting + conflict check below is
  // preserved, but "Add policy" cannot persist server-side, so it surfaces a
  // toast and closes rather than mutating data.
  const submit = () => {
    if (!canAdd) return;
    toast.message(`Drafted "${name.trim()}" — saving new policies isn't available yet`, {
      description: 'The v1 API has no create-policy endpoint. Manage enforcement on existing policies for now.',
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-violet-600 text-white flex items-center justify-center"><Bot className="h-4 w-4" /></span>
            New policy
          </DialogTitle>
          <DialogDescription>Describe the rule in plain English — the policy agent drafts it and checks it against your existing policies.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Conversational input */}
          <div>
            <Label className="text-xs text-slate-500">Describe the policy</Label>
            <Textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="e.g. Require a card on file when the remaining deductible is over $1,000…"
              className="mt-1.5 text-sm"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => setText(ex)} className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1 hover:border-brand-300 hover:text-slate-900">
                  {ex}
                </button>
              ))}
            </div>
          </div>

          {/* Interpreted draft */}
          {interpreted && (
            <div className="ai-surface p-3.5 space-y-3">
              <div className="flex items-center gap-1.5 text-[11px] text-violet-700 font-medium"><Wand2 className="h-3.5 w-3.5" />Interpreted policy <span className="text-slate-400 font-normal">· edit anything below</span></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-slate-500">Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-9 text-sm bg-white" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Scope</Label>
                  <Input value={scope} onChange={(e) => setScope(e.target.value)} className="mt-1 h-9 text-sm bg-white" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Enforcement</Label>
                <div className="mt-1 inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                  {ENF.map((e) => (
                    <button key={e} onClick={() => setEnf(e)} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${enfTone(e, enf === e)}`}>
                      {ENFORCEMENT_LABELS[e]}
                    </button>
                  ))}
                </div>
                <span className="ml-2 text-[11px] text-slate-400">{enf === 'off' ? 'inactive' : enf === 'warn' ? 'alert staff' : 'block until satisfied'}</span>
              </div>
            </div>
          )}

          {/* Conflict check */}
          {interpreted && (
            conflicts.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                <ShieldCheck className="h-4 w-4" />No conflicts with your existing policies.
              </div>
            ) : (
              <div className="space-y-2">
                {contradictions.map((c, i) => (
                  <div key={`c${i}`} className="flex items-start gap-2.5 text-sm bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
                    <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
                    <div><span className="font-semibold text-rose-900">Contradiction</span><div className="text-rose-800 mt-0.5">{c.reason}</div></div>
                  </div>
                ))}
                {overlaps.map((c, i) => (
                  <div key={`o${i}`} className="flex items-start gap-2.5 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div><span className="font-semibold text-amber-900">Overlap</span><div className="text-amber-800 mt-0.5">{c.reason}</div></div>
                  </div>
                ))}
                {contradictions.length > 0 && (
                  <label className="flex items-center gap-2 text-xs text-slate-600 px-1 pt-1 cursor-pointer">
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                    Add anyway — I understand this conflicts with an existing policy.
                  </label>
                )}
              </div>
            )
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => close(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canAdd} className="bg-brand-600 hover:bg-brand-700 text-white">
            <Check className="h-4 w-4 mr-1.5" />Add policy
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
